
import {
  Component,
  EventEmitter,
  HostListener,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import {
  IonButton,
  IonIcon,
  IonLabel,
  IonSegment,
  IonSegmentButton,
  IonSpinner
} from '@ionic/angular';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { addIcons } from 'ionicons';
import {
  closeOutline,
  desktopOutline,
  moonOutline,
  saveOutline,
  sunnyOutline
} from 'ionicons/icons';

import { LoggerService } from '../../services/logger.service';
import { ProfileService } from '../../services/profile.service';
import { ThemeService } from '../../services/theme.service';

type ThemeMode = 'auto' | 'light' | 'dark';

interface ThemeOption {
  mode: ThemeMode;
  label: string;
  description: string;
  icon: string;
}

@Component({
    selector: 'app-first-run-theme-prompt',
    imports: [
    IonButton,
    IonIcon,
    IonLabel,
    IonSegment,
    IonSegmentButton,
    IonSpinner
],
    templateUrl: './first-run-theme-prompt.component.html',
    styleUrls: ['./first-run-theme-prompt.component.scss']
})
export class FirstRunThemePromptComponent implements OnInit, OnDestroy {
  @Output() decided = new EventEmitter<void>();

  readonly themeOptions: readonly ThemeOption[] = [
    {
      mode: 'auto',
      label: 'System',
      description: 'Match this device',
      icon: 'desktop-outline'
    },
    {
      mode: 'light',
      label: 'Light',
      description: 'Always use light',
      icon: 'sunny-outline'
    },
    {
      mode: 'dark',
      label: 'Dark',
      description: 'Always use dark',
      icon: 'moon-outline'
    }
  ];

  selectedMode: ThemeMode = 'auto';
  isSaving = false;
  saveError = '';
  previewAnnouncement = 'System theme selected.';

  private readonly destroy$ = new Subject<void>();
  private readonly defaultAccentColor = '#0E5B3A';
  private readonly legacyDefaultAccentColors = ['#7c3aed', '#8b5cf6'];
  private accentColor = this.defaultAccentColor;
  private decisionEmitted = false;

  constructor(
    private readonly themeService: ThemeService,
    private readonly profileService: ProfileService,
    private readonly logger: LoggerService
  ) {
    addIcons({
      closeOutline,
      desktopOutline,
      moonOutline,
      saveOutline,
      sunnyOutline
    });
  }

  ngOnInit(): void {
    this.useProfileAccent(this.profileService.getCurrentProfile()?.AccentColor);
    this.themeService.applyTheme(this.selectedMode, this.accentColor);

    this.profileService.profile$
      .pipe(takeUntil(this.destroy$))
      .subscribe(profile => {
        if (this.useProfileAccent(profile?.AccentColor)) {
          this.themeService.applyTheme(this.selectedMode, this.accentColor);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onThemeChange(event: Event): void {
    const value = (event as CustomEvent<{ value?: unknown }>).detail?.value;
    if (!this.isThemeMode(value) || this.isSaving) {
      return;
    }

    this.selectedMode = value;
    this.saveError = '';
    this.themeService.applyTheme(value, this.accentColor);
    this.previewAnnouncement = `${this.labelFor(value)} theme previewed.`;

    this.logger.debug(
      'FirstRunThemePromptComponent',
      'onThemeChange',
      'First-run theme preview applied',
      { mode: value }
    );
  }

  async save(): Promise<void> {
    await this.persistDecision(this.selectedMode, 'save');
  }

  async dismiss(): Promise<void> {
    if (this.isSaving) {
      return;
    }

    this.selectedMode = 'auto';
    this.themeService.applyTheme('auto', this.accentColor);
    this.previewAnnouncement = 'System theme selected.';
    await this.persistDecision('auto', 'dismiss');
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscape(event: Event): void {
    if (this.isSaving || this.decisionEmitted) {
      return;
    }

    event.preventDefault();
    void this.dismiss();
  }

  private async persistDecision(mode: ThemeMode, source: 'save' | 'dismiss'): Promise<void> {
    if (this.isSaving || this.decisionEmitted) {
      return;
    }

    this.isSaving = true;
    this.saveError = '';

    try {
      await this.themeService.saveThemeToStorage(mode, this.accentColor);
      await firstValueFrom(
        this.profileService.updateThemePreferences(mode, this.accentColor)
      );

      this.decisionEmitted = true;
      this.logger.info(
        'FirstRunThemePromptComponent',
        'persistDecision',
        'First-run theme preference persisted',
        { mode, source }
      );
      this.decided.emit();
    } catch (error) {
      this.saveError = 'We could not save your theme. Please try again.';
      this.logger.error(
        'FirstRunThemePromptComponent',
        'persistDecision',
        'Failed to persist first-run theme preference',
        { error, mode, source }
      );
    } finally {
      this.isSaving = false;
    }
  }

  private useProfileAccent(accentColor: string | null | undefined): boolean {
    if (!/^#[0-9a-f]{6}$/i.test(accentColor ?? '')) {
      return false;
    }

    const normalizedAccentColor = this.legacyDefaultAccentColors.includes(accentColor!.toLowerCase())
      ? this.defaultAccentColor
      : accentColor!;
    if (normalizedAccentColor === this.accentColor) {
      return false;
    }

    this.accentColor = normalizedAccentColor;
    return true;
  }

  private isThemeMode(value: unknown): value is ThemeMode {
    return value === 'auto' || value === 'light' || value === 'dark';
  }

  private labelFor(mode: ThemeMode): string {
    return this.themeOptions.find(option => option.mode === mode)?.label ?? 'System';
  }
}
